import styled from 'styled-components';
import { formatDateTime } from '@equifinance/shared/utils';
import { PropsWithChildren } from 'react';
import { Flex } from '@equifinance/shared';
import { ApplicationTask, ApplicationTaskStatus } from '../../../contexts/applications';
import {
  BodyRow,
  GroupList,
  GroupRow,
  TaskOverviewContainer,
  HeadRow,
  OverviewBody,
  OverviewCell,
  OverviewHeader,
  groupItemsByStage,
} from './ExpandableContent';

type StatusDisplayType = 'primary' | 'inverted' | 'light' | 'success' | 'error';

function getStatusType(status: ApplicationTask['status']): StatusDisplayType {
  if (['Approved', 'ApprovedWithException'].includes(status)) {
    return 'success';
  }
  if (['Rejected'].includes(status)) {
    return 'error';
  }
  if (['MoreInformationRequired'].includes(status)) {
    return 'primary';
  }
  if (['OutstandingForBroker'].includes(status)) {
    return 'inverted';
  }
  return 'light';
}

const TaskStatusWrapper = styled(Flex)`${({ theme }) => `
  gap: ${theme.padding[2]};
  align-items: center;
`}`;

const StatusDisplay = styled(Flex)<{ type: StatusDisplayType }>`${({ type, theme }) => `
  align-items: center;
  padding: ${theme.padding[1]} ${theme.padding[2]};
  font-weight: ${theme.fontWeight.bold};
  color: ${theme.colours.neutral[10]};
  border-radius: ${theme.borderRadius[1]};
  > i {
    font-size: ${theme.fontSize[3]};
  }
  gap: ${theme.borderWidth[2]};
  ${type === 'inverted' && `
    border: ${theme.borderWidth[1]} solid ${theme.colours.primary[4]};
    background: transparent;
    color: ${theme.colours.primary[1]};
  `};
  ${type === 'light' && `
    background: ${theme.colours.primary[10]};
    color: ${theme.colours.primary[1]};
  `};
  ${type === 'primary' && `
    background: ${theme.colours.primary[3]};
  `};
  ${type === 'success' && `
    padding: ${theme.borderWidth[2]} ${theme.padding[2]};
    line-height: ${theme.fontSize[4]};
    background: ${theme.colours.success[1]};
  `};
  ${type === 'error' && `
    padding: ${theme.borderWidth[2]} ${theme.padding[2]};
    line-height: ${theme.fontSize[4]};
    background: ${theme.colours.danger[1]};
  `};
`}`;

const AlertIcon = styled.i`${({ theme }) => `
  color: ${theme.colours.danger[1]};
  font-size: ${theme.fontSize[7]};
`}`;

function TaskStatusCell({ updated, type, children }: PropsWithChildren<{ updated: boolean, type: StatusDisplayType }>) {
  return (
    <TaskStatusWrapper>
      {updated && (
        <AlertIcon className="mi-notification" />
      )}
      {children ? (
        <StatusDisplay type={type}>
          {type === 'success' && (
          <i className="mi-circle-check" />
          )}
          {type === 'error' && (
          <i className="mi-circle-error" />
          )}
          {children}
        </StatusDisplay>
      ) : '-'}
    </TaskStatusWrapper>
  );
}

export default function TaskOverview({ tasks }: { tasks?: ApplicationTask[]}) {
  const groupedTask = groupItemsByStage(
    tasks || [],
    (task) => ({
      status: task.status,
      title: task.title?.trim() || '',
      updated: !!task.updated,
      statusType: getStatusType(task.status),
      lastUpdated: task.lastUpdated ? formatDateTime(task.lastUpdated, 'DD/MM/YYYY, HH:mm:ss') : '',
    }),
  );

  return (
    <TaskOverviewContainer>
      <OverviewHeader>Task overview</OverviewHeader>
      <OverviewBody>
        <HeadRow>
          <OverviewCell>Stage</OverviewCell>
          <OverviewCell>Task title</OverviewCell>
          <OverviewCell>Date and time</OverviewCell>
        </HeadRow>
        <GroupList>
          {Object.entries(groupedTask).map(([group, tasks]) => (
            <div key={group}>
              <GroupRow>
                <OverviewCell>{group}</OverviewCell>
              </GroupRow>
              {tasks.map((task) => (
                <BodyRow key={task.lastUpdated}>
                  <OverviewCell>
                    <TaskStatusCell updated={task.updated} type={task.statusType}>
                      {ApplicationTaskStatus[task.status]}
                    </TaskStatusCell>
                  </OverviewCell>
                  <OverviewCell>{task.title || '-'}</OverviewCell>
                  <OverviewCell fontWeight="normal">{task.lastUpdated || '-'}</OverviewCell>
                </BodyRow>
              ))}
            </div>
          ))}
        </GroupList>
      </OverviewBody>
    </TaskOverviewContainer>
  );
}
